import { getShipmentTagIds, resolveShipmentAssignedTags } from '@/services/tagsCatalog'
import { isProductReleaseV1Enabled } from '@/utility/productReleaseStatsigGateConstants'
import { buildShipmentSelectFields } from './customerOrderIdStatsigGateConstants'
import { isShipmentsTagPivotEnabled } from './releaseStatsigGateConstants'

/**
 * CC-6037: resolve the temperature shipment list RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'filter_temperature_shipments_view_v3' | 'filter_temperature_shipments_view_v2' | 'filter_temperature_shipments_view'} RPC name for the active gates.
 */
export function getTemperatureShipmentsViewRpcName(statsigClient) {
  if (isProductReleaseV1Enabled(statsigClient)) {
    return 'filter_temperature_shipments_view_v4'
  }

  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'filter_temperature_shipments_view_v3'
  }

  if (statsigClient?.checkGate?.('shipment-list-enhancements-v1')) {
    return 'filter_temperature_shipments_view_v2'
  }

  return 'filter_temperature_shipments_view'
}

/**
 * CC-6037: resolve the command center shipment list RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'filter_shipments_view_v3' | 'filter_shipments_view_v2' | 'filter_shipments_view'} RPC name for the active gates.
 */
export function getCommandCenterShipmentsViewRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'filter_shipments_view_v3'
  }

  if (statsigClient?.checkGate?.('shipment-list-enhancements-v1')) {
    return 'filter_shipments_view_v2'
  }

  return 'filter_shipments_view'
}

/**
 * CC-6037: resolve the command center shipment map RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'filter_shipments_map_view_v3' | 'filter_shipments_map_view'} RPC name for the active gates.
 */
export function getCommandCenterShipmentsMapViewRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'filter_shipments_map_view_v3'
  }

  return 'filter_shipments_map_view'
}

/**
 * CC-6037: resolve grouped shipment pagination RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'fetch_shipments_by_group_v3' | 'fetch_shipments_by_group'} RPC name for the active gates.
 */
export function getFetchShipmentsByGroupRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'fetch_shipments_by_group_v3'
  }

  return 'fetch_shipments_by_group'
}

/**
 * CC-6037: resolve grouped temperature shipment pagination RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'fetch_temp_shipments_by_group_v3' | 'fetch_temp_shipments_by_group'} RPC name for the active gates.
 */
export function getFetchTempShipmentsByGroupRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'fetch_temp_shipments_by_group_v3'
  }

  return 'fetch_temp_shipments_by_group'
}

/**
 * CC-6037: resolve shipment UAG user-id lookup RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'get_shipment_uag_user_ids_v3' | 'get_shipment_uag_user_ids_v1'} RPC name for the active gates.
 */
export function getShipmentUagUserIdsRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'get_shipment_uag_user_ids_v3'
  }

  return 'get_shipment_uag_user_ids_v1'
}

/**
 * CC-6037: resolve Insights Lightdash filter conversion RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @param {string | null | undefined} insightsType
 * @returns {'convert_shipment_view_filters_to_lightdash_v3' | 'convert_shipment_view_filters_to_lightdash_v2' | 'convert_shipment_view_filters_to_lightdash'} RPC name for the active gates.
 */
export function getConvertShipmentViewFiltersToLightdashRpcName(statsigClient, insightsType) {
  if (insightsType === 'lane-performance') {
    if (isShipmentsTagPivotEnabled(statsigClient)) {
      return 'convert_shipment_view_filters_to_lightdash_v3'
    }
    return 'convert_shipment_view_filters_to_lightdash_v2'
  }

  return 'convert_shipment_view_filters_to_lightdash'
}

/**
 * CC-6037: resolve realtime view-membership check RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'fn_check_shipment_in_view_v3' | 'fn_check_shipment_in_view'} RPC name for the active gates.
 */
export function getCheckShipmentInViewRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'fn_check_shipment_in_view_v3'
  }

  return 'fn_check_shipment_in_view'
}

/**
 * CC-6037: resolve assigned-items count RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'get_assigned_items_count_v3' | 'get_assigned_items_count_v1'} RPC name for the active gates.
 */
export function getAssignedItemsCountRpcName(statsigClient) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'get_assigned_items_count_v3'
  }

  return 'get_assigned_items_count_v1'
}

/**
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {boolean} Whether CC-6037 tag pivot reads are enabled.
 */
export function isShipmentListTagPivotEnabled(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
}

/**
 * CC-6037: resolve shipment-summary edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'shipment-summary-v3' | 'shipment-summary'} Edge function name for the active gates.
 */
export function getShipmentSummaryApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient) ? 'shipment-summary-v3' : 'shipment-summary'
}

/**
 * CC-6037: resolve send-csv-report edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'send-csv-report-v3' | 'send-csv-report-v1'} Edge function name for the active gates.
 */
export function getSendCsvReportApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient) ? 'send-csv-report-v3' : 'send-csv-report-v1'
}

/**
 * CC-6037: resolve shipment activity-history CSV export edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'download-activity-history-shipments-csv-v3' | 'download-activity-history-shipments-csv-v1'} Edge function name for the active gates.
 */
export function getDownloadActivityHistoryShipmentsCsvApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
    ? 'download-activity-history-shipments-csv-v3'
    : 'download-activity-history-shipments-csv-v1'
}

/**
 * CC-6037: resolve temp-release reviewer eligibility edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'get-shipment-temprelease-reviewers-v3' | 'get-shipment-temprelease-reviewers-v2'} Edge function name for the active gates.
 */
export function getShipmentTempReleaseReviewersApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
    ? 'get-shipment-temprelease-reviewers-v3'
    : 'get-shipment-temprelease-reviewers-v2'
}

/**
 * CC-6037: resolve create user access group edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'create_user_access_group_v3' | 'create_user_access_group_v2'} Edge function name for the active gates.
 */
export function getCreateUserAccessGroupApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
    ? 'create_user_access_group_v3'
    : 'create_user_access_group_v2'
}

/**
 * CC-6037: resolve update user access group edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'update_user_access_group_v3' | 'update_user_access_group_v2'} Edge function name for the active gates.
 */
export function getUpdateUserAccessGroupApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
    ? 'update_user_access_group_v3'
    : 'update_user_access_group_v2'
}

/**
 * CC-6037: resolve delete user access group edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'delete_user_access_group_v3' | 'delete_user_access_group_v2'} Edge function name for the active gates.
 */
export function getDeleteUserAccessGroupApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
    ? 'delete_user_access_group_v3'
    : 'delete_user_access_group_v2'
}

/**
 * CC-6037: resolve move users to user access group edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'move_users_to_user_access_group_v3' | 'move_users_to_user_access_group_v2'} Edge function name for the active gates.
 */
export function getMoveUsersToUserAccessGroupApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient)
    ? 'move_users_to_user_access_group_v3'
    : 'move_users_to_user_access_group_v2'
}

/**
 * CC-6037: resolve tag update edge function for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @returns {'tag-update-v3' | 'tag-update-v1'} Edge function name for the active gates.
 */
export function getTagUpdateApiName(statsigClient) {
  return isShipmentsTagPivotEnabled(statsigClient) ? 'tag-update-v3' : 'tag-update-v1'
}

/**
 * CC-6037: whether tags-table reads should include explicit org scope.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @param {{ orgId?: string | null }} [options]
 * @returns {boolean} True when org_id should be sent on tags queries.
 */
export function shouldScopeTagsTableQueryByOrgId(statsigClient, { orgId = null } = {}) {
  const trimmedOrgId = String(orgId || '').trim()
  return isShipmentsTagPivotEnabled(statsigClient) && trimmedOrgId.length > 0
}

/**
 * CC-6037: append org_id filter to a Supabase tags-table query when the pivot gate is on.
 * @param {object | null | undefined} query
 * @param {string | null | undefined} orgId
 * @param {{ statsigClient?: import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined }} [options]
 * @returns {object | null | undefined} Query with optional org_id filter applied.
 */
export function applyTagsTableOrgIdFilter(query, orgId, { statsigClient = null } = {}) {
  if (!query || !shouldScopeTagsTableQueryByOrgId(statsigClient, { orgId })) {
    return query
  }

  return query.eq('org_id', String(orgId).trim())
}

/**
 * CC-6037: build optional org scope params for tags RPCs (e.g. get_tags_by_group_v3).
 * @param {string | null | undefined} orgId
 * @param {{ statsigClient?: import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined }} [options]
 * @returns {{ p_org_id?: string }} RPC params when org scoping applies.
 */
export function buildTagsRpcOrgParams(orgId, { statsigClient = null } = {}) {
  if (!shouldScopeTagsTableQueryByOrgId(statsigClient, { orgId })) {
    return {}
  }

  return { p_org_id: String(orgId).trim() }
}

/**
 * Gate-aware tag column for shipment row selects (details, QDC, map popup, etc.).
 * Gate off → legacy embedded `assigned_tags`. Gate on → UUID pivot `tag_ids`.
 * @param {{ tagPivotEnabled?: boolean }} [options]
 * @returns {'assigned_tags' | 'tag_ids'} Supabase select column name.
 */
export function buildShipmentTagSelectColumn({ tagPivotEnabled = false } = {}) {
  return tagPivotEnabled ? 'tag_ids' : 'assigned_tags'
}

/**
 * Supabase select clause for CC shipment rows loaded by temperature release surfaces.
 * Gate off → `assigned_tags`; gate on → `tag_ids` (aligned with CC Shipment Details).
 * @param {{ tagPivotEnabled?: boolean }} [options]
 * @returns {string} PostgREST select clause.
 */
export function buildTempReleaseCcShipmentSelectClause({ tagPivotEnabled = false } = {}) {
  const shipmentTagSelectColumn = buildShipmentTagSelectColumn({ tagPivotEnabled })

  return buildShipmentSelectFields(
    `
                    display_shipment_id,
                    handling_units,
                    shipment_primary_state,
                    shipment_timing_status,
                    origin,
                    org_id,
                    destination,
                    shipment_start_timestamp,
                    planned_arrival_timestamp,
                    updated_arrival_timestamp,
                    delayed_by,
                    carriers,
                    lane_profile,
                    description,
                    created_at,
                    order_number,
                    carrier_tracking_number,
                    asn_number,
                    bill_of_lading,
                    vehicle_id,
                    container_id,
                    vessel_imo_number,
                    flight_number,
                    notification_recipients,
                    hawb,
                    mawb,
                    active,
                    passive,
                    product_profiles,
                    batch_id,
                    ${shipmentTagSelectColumn},
                    shipment_uuid,
                    source,
                    encrypted_code,
                    created_by,
                    completed_at,
                    requested_dropoff_at,
                    requested_pickup_at,
                    consignee_contact,
                    shipment_processing_state,
                    flights_urls,
                    vessel_imo_links,
                    temperature_status,
                    translation_languages,
                    shipment_risk_lvl
                `,
  )
}

/**
 * CC-6037: resolve grouped tags list RPC for the active feature gates.
 * @param {import('@statsig/client-core').StatsigClient | { checkGate?: (gate: string) => boolean } | null | undefined} statsigClient
 * @param {{ legacyRpcName?: string }} [options]
 * @returns {'get_tags_by_group_v3' | string} RPC name for grouped tags list fetches.
 */
export function getTagsByGroupRpcName(
  statsigClient,
  { legacyRpcName = 'get_tags_by_group_v1' } = {},
) {
  if (isShipmentsTagPivotEnabled(statsigClient)) {
    return 'get_tags_by_group_v3'
  }

  return legacyRpcName
}

const isUuidLikeTagLabel = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())

/**
 * Tag-group list headers: command-center-v3 / tag-pivot RPCs return tag UUIDs in
 * `primary_group`. Prefer the catalog-backed tag name so the accordion shows values.
 * @param {object | null | undefined} group
 * @param {{ enabled?: boolean }} [options]
 * @returns {string} Display label for the group section title.
 */
export function resolveTagGroupSectionTitle(group, { enabled = false } = {}) {
  const fallback = group?.primary_group || group?.primary_group_id || ''
  if (!enabled) return fallback

  const tagName = group?.primary_group_obj?.tag_name
  if (tagName && !isUuidLikeTagLabel(tagName)) return tagName

  return fallback
}

/**
 * Resolve shipment tags into `{ tag_id }` objects for UAG feature-access checks.
 * Gate on → catalog-backed tags from `tag_ids`; gate off → legacy `assigned_tags` objects.
 * @param {object | null | undefined} shipment
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {Array<object>} Tag references for permission scoping.
 */
export function resolveShipmentTagsForFeatureAccess(shipment, orgId, { enabled = false } = {}) {
  if (!shipment) return []

  if (enabled) {
    return getShipmentTagIds(shipment).map((tagId) => ({ tag_id: tagId, id: tagId }))
  }

  if (Array.isArray(shipment.assigned_tags) && shipment.assigned_tags.length > 0) {
    return shipment.assigned_tags
  }

  return getShipmentTagIds(shipment).map((tagId) => ({ tag_id: tagId, id: tagId }))
}

export function resolveShipmentListDisplayTags(shipment, orgId, { enabled = false } = {}) {
  if (enabled) {
    return resolveShipmentAssignedTags(shipment, orgId)
  }

  if (!Array.isArray(shipment?.assigned_tags) || shipment.assigned_tags.length === 0) {
    return []
  }

  return shipment.assigned_tags.map((tag) => ({
    id: tag.id ?? tag.tag_id,
    tag_id: tag.tag_id ?? tag.id,
    tag_name: tag.tag_name,
    color_code: tag.color_code,
    group_name: tag.group_name ?? null,
    pinned: Boolean(tag.pinned ?? tag.pinned_tag),
  }))
}

/**
 * Hydrate `assigned_tags` on a shipment row from the org tags catalog.
 * @param {object | null | undefined} shipment
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {object | null | undefined} Shipment row with catalog-backed `assigned_tags`.
 */
export function enrichShipmentWithAssignedTags(shipment, orgId, { enabled = false } = {}) {
  if (!enabled || !shipment) return shipment

  return {
    ...shipment,
    assigned_tags: resolveShipmentAssignedTags(shipment, orgId),
  }
}

/**
 * Hydrate catalog-backed shipment tag metadata on assigned-items count RPC rows.
 * @param {Array<object>|null|undefined} items
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {Array<object>|null|undefined} Assigned-items blocks with shipment rows enriched.
 */
export function enrichAssignedItemsCountResponse(items, orgId, { enabled = false } = {}) {
  if (!enabled || !Array.isArray(items)) {
    return items
  }

  return items.map((block) => {
    if (block?.entity_type !== 'shipments' || !Array.isArray(block.data)) {
      return block
    }

    return {
      ...block,
      data: block.data.map((row) => enrichShipmentWithAssignedTags(row, orgId, { enabled: true })),
    }
  })
}

/**
 * Hydrate catalog-backed `assigned_tags` on a shipment row for edit/details UI.
 * @param {object | null | undefined} shipment
 * @param {string | null | undefined} orgId
 * @param {{ tagPivotEnabled?: boolean }} [options]
 * @returns {object | null | undefined} Shipment row with catalog-backed `assigned_tags`.
 */
export function prepareShipmentRowTagsForDisplay(
  shipment,
  orgId,
  { tagPivotEnabled = false } = {},
) {
  return enrichShipmentWithAssignedTags(shipment, orgId, { enabled: tagPivotEnabled })
}

/**
 * Re-resolve tag metadata on grouped shipment list rows from the current catalog snapshot.
 * @param {Array<object>} groups
 * @param {string | null | undefined} orgId
 * @param {string} [groupItemName]
 * @param {{ enabled?: boolean }} [options]
 * @returns {Array<object>} Grouped list rows with refreshed tag metadata.
 */
export function reEnrichGroupedShipmentsListTags(
  groups,
  orgId,
  groupItemName = '',
  { enabled = false } = {},
) {
  if (!enabled || !Array.isArray(groups)) {
    return groups
  }

  return groups.map((group) => {
    const shipments = (group.shipments || []).map((shipment) =>
      enrichShipmentWithAssignedTags(shipment, orgId, { enabled: true }),
    )

    let primary_group_obj = group.primary_group_obj
    if (
      (groupItemName === 'assigned_tags' || groupItemName === 'tag_group_name') &&
      group.primary_group_id
    ) {
      primary_group_obj =
        shipments[0]?.assigned_tags?.find(
          (tag) => String(tag.tag_id || tag.id) === String(group.primary_group_id),
        ) ||
        resolveShipmentAssignedTags({ tag_ids: [group.primary_group_id] }, orgId)[0] ||
        primary_group_obj
    }

    return {
      ...group,
      shipments,
      primary_group_obj,
    }
  })
}

/**
 * Hydrate grouped shipment list RPC rows with catalog-backed tag metadata.
 * @param {object | null | undefined} data
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {Array<object>} List groups with catalog-backed tags.
 */
function enrichShipmentsViewGroups(data, orgId, { enabled = false } = {}) {
  if (!enabled || !Array.isArray(data?.groups)) {
    return data?.groups || []
  }

  return reEnrichGroupedShipmentsListTags(data.groups, orgId, data?.group_item_name || '', {
    enabled: true,
  })
}

/**
 * Hydrate grouped temperature shipment list RPC rows with catalog-backed tag metadata.
 * @param {object | null | undefined} data
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {Array<object>} Temperature list groups with catalog-backed tags.
 */
export function enrichTemperatureShipmentsViewGroups(data, orgId, { enabled = false } = {}) {
  return enrichShipmentsViewGroups(data, orgId, { enabled })
}

/**
 * Hydrate grouped command center shipment list RPC rows with catalog-backed tag metadata.
 * @param {object | null | undefined} data
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {Array<object>} Command center list groups with catalog-backed tags.
 */
export function enrichCommandCenterShipmentsViewGroups(data, orgId, { enabled = false } = {}) {
  return enrichShipmentsViewGroups(data, orgId, { enabled })
}

/**
 * @param {object | null | undefined} shipment
 * @param {string | null | undefined} orgId
 * @param {{ enabled?: boolean }} [options]
 * @returns {string} Comma-separated tag names for CSV export.
 */
export function formatShipmentTagsForCsv(shipment, orgId, { enabled = false } = {}) {
  return resolveShipmentListDisplayTags(shipment, orgId, { enabled })
    .map((tag) => tag?.tag_name)
    .filter(Boolean)
    .join(', ')
}
